import React, { useState } from 'react';
import { X, Search, Plus } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

interface AddTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddToken: (token: any) => void;
}

const popularTokens = [
  { symbol: 'BTC', name: 'Bitcoin', address: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', icon: '₿', color: 'from-orange-500 to-yellow-500' },
  { symbol: 'BNB', name: 'Binance Coin', address: '0xb8c77482e45f1f44de1745f52c74426c631bdd52', icon: 'B', color: 'from-yellow-500 to-yellow-600' },
  { symbol: 'ADA', name: 'Cardano', address: '0x3ee2200efb3400fabb9aacf31297cbdd1d435d47', icon: 'A', color: 'from-blue-500 to-blue-600' },
  { symbol: 'DOT', name: 'Polkadot', address: '0x7083609fce4d1d8dc0c979aab8c869ea2c873402', icon: '●', color: 'from-pink-500 to-red-500' },
  { symbol: 'LINK', name: 'Chainlink', address: '0x514910771af9ca656af840dff83e8264ecf986ca', icon: '🔗', color: 'from-blue-600 to-indigo-600' },
  { symbol: 'UNI', name: 'Uniswap', address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', icon: '🦄', color: 'from-pink-500 to-purple-500' }
];

export function AddTokenModal({ isOpen, onClose, onAddToken }: AddTokenModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [customAddress, setCustomAddress] = useState('');
  const [customSymbol, setCustomSymbol] = useState('');
  const [customName, setCustomName] = useState('');
  const [activeTab, setActiveTab] = useState<'popular' | 'custom'>('popular');

  if (!isOpen) return null;

  const filteredTokens = popularTokens.filter(token =>
    token.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    token.symbol.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddPopularToken = (token: any) => {
    onAddToken(token);
    toast.success(`${token.symbol} added to your wallet!`);
    onClose();
  };

  const handleAddCustomToken = () => {
    if (!customAddress || !customSymbol || !customName) {
      toast.error('Please fill in all fields');
      return;
    }

    const customToken = {
      symbol: customSymbol.toUpperCase(),
      name: customName,
      address: customAddress,
      icon: customSymbol.charAt(0).toUpperCase(),
      color: 'from-gray-500 to-gray-600'
    };

    onAddToken(customToken);
    toast.success(`${customToken.symbol} added to your wallet!`);
    setCustomAddress('');
    setCustomSymbol('');
    setCustomName('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-2xl max-w-sm w-full max-h-[90vh] overflow-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="font-bold text-lg text-foreground">Add Token</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex p-2 m-4 bg-muted rounded-xl">
          <button
            onClick={() => setActiveTab('popular')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              activeTab === 'popular'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Popular
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              activeTab === 'custom'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Custom
          </button>
        </div>

        {/* Content */}
        <div className="p-6 pt-0">
          {activeTab === 'popular' ? (
            <div className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search tokens..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Token List */}
              <div className="space-y-2 max-h-64 overflow-auto">
                {filteredTokens.map((token) => (
                  <button
                    key={token.symbol}
                    onClick={() => handleAddPopularToken(token)}
                    className="w-full flex items-center space-x-3 p-3 bg-muted rounded-xl hover:bg-accent transition-colors"
                  >
                    <div className={`w-10 h-10 bg-gradient-to-r ${token.color} rounded-full flex items-center justify-center text-white font-bold`}>
                      {token.icon}
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-foreground">{token.symbol}</p>
                      <p className="text-sm text-muted-foreground">{token.name}</p>
                    </div>
                    <Plus className="w-5 h-5 text-primary" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="contract-address">Contract Address</Label>
                <Input
                  id="contract-address"
                  placeholder="0x..."
                  value={customAddress}
                  onChange={(e) => setCustomAddress(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="token-symbol">Token Symbol</Label>
                <Input
                  id="token-symbol"
                  placeholder="e.g. ETH"
                  value={customSymbol}
                  onChange={(e) => setCustomSymbol(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="token-name">Token Name</Label>
                <Input
                  id="token-name"
                  placeholder="e.g. Ethereum"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="mt-1"
                />
              </div>

              <button
                onClick={handleAddCustomToken}
                className="w-full bg-primary text-primary-foreground py-3 rounded-xl font-medium hover:bg-primary/90 transition-colors"
              >
                Add Custom Token
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}